"use client";

// ── Your own code, in the result sheet (`EX-SCB09`) ──────────────────────────
// A contestant could open their own program only by pressing their own result
// on the standings (`ResultSheet`, `EX-SCB05`); the contest's Submissions tab
// and My Submissions showed a verdict and nothing more (the founder,
// 2026-10-09). This is that sheet for one problem of one contest, opened from
// a row: every attempt the caller made on the problem on the left, starting on
// the row that was pressed, and on the right the verdict and the coloured
// program.
//
// **The caller's own reads, nothing else.** The attempts are the contest's own
// list narrowed to the letter (`GET /v1/contests/:slug/submissions?letter=`)
// and the program its own read (`GET .../submissions/:id/source`), so the
// verdict says how many tests passed and never which (`count_only`, as on the
// contest page), and a program opens only for the person who wrote it.

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowUpRight, Clock, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgramSourceView } from "@/components/code/ProgramSourceView";
import { SubmissionResultDetail } from "@/features/judge/components/workspace/SubmissionResult";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { useMyContestSubmissionSource, useMyContestSubmissions } from "../hooks";
import { formatContestMinute } from "../lib/format";
import type { ContestSubmissionItem } from "../types";
import { SHORT_VERDICT, toneOf } from "./ResultSheet";

/** Attempts on one problem are read in one page, as the result sheet reads them. */
const ATTEMPT_LIMIT = 100;

export function OwnSubmissionSheet({
  slug,
  letter,
  problemTitle,
  balloonHex,
  contestTitle,
  contestStartAt,
  initialSubmissionId,
  contestHref,
  onClose,
  virtual = false,
}: {
  slug: string;
  letter: string;
  problemTitle?: string | null;
  balloonHex?: string | null;
  contestTitle: string;
  contestStartAt: string | null;
  /** The row that was pressed; the sheet starts on it. */
  initialSubmissionId: string;
  /** "Open the contest", from a page that is not the contest's own. */
  contestHref?: string;
  onClose: () => void;
  /** `EX-VRT01`: the rows are a virtual try's; `contestStartAt` is then the try's start. */
  virtual?: boolean;
}) {
  const t = useTranslations("contest.standings");
  const tPage = useTranslations("contest.page");
  const tJudge = useTranslations("judge");
  const [chosen, setChosen] = useState(initialSubmissionId);

  const list = useMyContestSubmissions(slug, true, { letter, limit: ATTEMPT_LIMIT }, virtual);
  const attempts: ContestSubmissionItem[] = [...(list.data?.items ?? [])].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  );
  const total = list.data?.total ?? attempts.length;
  const selected = attempts.find((attempt) => attempt.submissionId === chosen) ?? null;
  // The pressed row is read at once, before the list arrives.
  const selectedId = selected?.submissionId ?? chosen;
  const source = useMyContestSubmissionSource(slug, selectedId, virtual);
  const program = source.data && source.data.submissionId === selectedId ? source.data : null;

  const startMs = contestStartAt ? Date.parse(contestStartAt) : NaN;
  const minuteOf = (iso: string) =>
    Number.isFinite(startMs) && Date.parse(iso) >= startMs ? formatContestMinute((Date.parse(iso) - startMs) / 60_000) : null;

  const verdictName = (attempt: ContestSubmissionItem) =>
    attempt.status !== "completed" || !attempt.verdict ? t("sheetJudging") : tJudge(`verdict.${attempt.verdict}`);

  const detailLine = (attempt: ContestSubmissionItem): string => {
    if (attempt.status !== "completed" || !attempt.verdict) return t("sheetJudging");
    const name = verdictName(attempt);
    if (!attempt.hidden || attempt.verdict === "compilation_error") return name;
    return attempt.verdict === "accepted"
      ? t("sheetAllPassed", { total: attempt.hidden.totalCount })
      : t("sheetPassedOf", { passed: attempt.hidden.passedCount, total: attempt.hidden.totalCount });
  };

  const metaLine = (attempt: ContestSubmissionItem): string => {
    const at = minuteOf(attempt.createdAt);
    return [
      attempt.timeMs !== undefined ? t("sheetTime", { seconds: Math.round(attempt.timeMs / 10) / 100 }) : null,
      attempt.memoryKb !== undefined ? t("sheetMemory", { mb: Math.round(attempt.memoryKb / 102.4) / 10 }) : null,
      attempt.languageKey ? formatRuntimeLanguageName(attempt.languageKey) : null,
      at ? t("sheetAt", { at }) : null,
    ]
      .filter((part): part is string => part !== null)
      .join(" · ");
  };

  const title = problemTitle ? t("sheetTitle", { letter, title: problemTitle }) : letter;
  const compileOutput = selected?.verdict === "compilation_error" ? (selected.compileOutput ?? null) : null;

  const leading = (
    <span className="relative grid h-[38px] w-[38px] place-items-center rounded-[11px] bg-neutral-900 text-base font-bold text-white">
      {letter}
      {balloonHex ? (
        <span
          aria-hidden
          className="absolute -right-1 -top-1 h-[13px] w-[13px] rounded-full ring-2 ring-white"
          style={{ backgroundColor: balloonHex }}
        />
      ) : null}
    </span>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={t("sheetOwnSub", { contest: contestTitle, count: total })}
      closeLabel={t("sheetClose")}
      size="sheet"
      leading={leading}
    >
      <div data-own-sheet className="flex min-h-0 flex-1 flex-col sm:flex-row">
        <nav
          aria-label={t("sheetAttempts")}
          className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-neutral-200 bg-neutral-50 p-3 sm:w-[236px] sm:flex-col sm:overflow-y-auto sm:overflow-x-visible sm:border-b-0 sm:border-r"
        >
          <p className="hidden px-1.5 pb-1 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-500 sm:block">
            {t("sheetAttempts")}
          </p>
          {list.isPending ? (
            <>
              <Skeleton className="h-10 w-28 shrink-0 rounded-xl sm:w-full" />
              <Skeleton className="h-10 w-28 shrink-0 rounded-xl sm:w-full" />
            </>
          ) : list.isError ? (
            <p className="px-1.5 text-sm text-neutral-500">{t("sheetFailed")}</p>
          ) : (
            attempts.map((attempt, index) => {
              const on = attempt.submissionId === selectedId;
              const at = minuteOf(attempt.createdAt);
              return (
                <Button
                  key={attempt.submissionId}
                  type="button"
                  variant={null}
                  size={null}
                  aria-pressed={on}
                  title={verdictName(attempt)}
                  onClick={() => setChosen(attempt.submissionId)}
                  className={cn(
                    "grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-x-2.5 rounded-xl px-[11px] py-2.5 text-left text-[13px] font-medium text-neutral-900 transition-colors duration-150 hover:bg-white",
                    on && "bg-white shadow-sm ring-1 ring-neutral-200",
                  )}
                >
                  <span className="font-bold text-neutral-500">{`#${index + 1}`}</span>
                  <span className="font-bold tabular-nums">{at ?? ""}</span>
                  <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold", toneOf({ status: attempt.status, verdict: attempt.verdict ?? null }, "light"))}>
                    {attempt.status !== "completed" ? <Clock className="h-3 w-3" aria-hidden /> : null}
                    {attempt.status === "completed" && attempt.verdict ? SHORT_VERDICT[attempt.verdict] : null}
                  </span>
                </Button>
              );
            })
          )}
        </nav>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4 sm:px-[18px]">
          {selected ? (
            <div className="flex flex-wrap items-center gap-3.5">
              <span className={cn("whitespace-nowrap rounded-full px-3.5 py-[7px] text-sm font-bold", toneOf({ status: selected.status, verdict: selected.verdict ?? null }, "light"))}>
                {verdictName(selected)}
              </span>
              <div className="min-w-0">
                <p data-sheet-detail className="text-[14.5px] font-bold text-neutral-900">
                  {detailLine(selected)}
                </p>
                <p className="mt-0.5 text-[12.5px] text-neutral-500">{metaLine(selected)}</p>
              </div>
            </div>
          ) : null}
          {compileOutput ? <SubmissionResultDetail result={{ compileOutput }} /> : null}
          {source.isError ? (
            <p className="text-sm text-neutral-500">{t("sheetProgramFailed")}</p>
          ) : program ? (
            <ProgramSourceView
              source={program.sourceCode}
              languageKey={program.languageKey}
              languageName={program.languageKey ? formatRuntimeLanguageName(program.languageKey) : null}
              languageVersion={program.languageVersion}
              labels={{
                lines: tPage("programLines", { count: program.sourceCode.split("\n").length }),
                wrap: tPage("programWrap"),
                copy: tPage("programCopy"),
                copied: tPage("programCopied"),
              }}
            />
          ) : (
            <Skeleton className="h-60 w-full rounded-xl" />
          )}
        </div>
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t border-neutral-200 px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1 space-y-0.5 text-[12.5px] text-neutral-500">
          <p>
            <Lock className="mr-1.5 inline h-[13px] w-[13px] align-[-2px]" aria-hidden />
            {t("sheetNoteOwn")}
          </p>
          {total > attempts.length ? <p>{t("sheetMore", { shown: attempts.length, total })}</p> : null}
        </div>
        {contestHref ? (
          <Link href={contestHref} className={buttonVariants({ variant: "outline", size: "sm" })}>
            {t("sheetOpenContest")}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : null}
      </footer>
    </Dialog>
  );
}
